import { useState } from 'react';

// Section 10 — FAQ accordion + final CTA.
// NOTE TO AJAYE: FAQ copy is a starting point covering predictable adult-learner
// concerns — refine in your voice before launch.
const FAQS = [
  {
    q: 'What certifications are available?',
    a: 'CompTIA A+, Network+, and Security+ are live at early access launch — full tracks, full adaptive coverage. Microsoft AZ-900 and AZ-104 are now live as well — selectable, startable, and fully seeded. AB-900 (Copilot & Agent Administration) is now in beta testing. MS-102 is queued for fast-follow. New tracks get added based on member demand.',
  },
  {
    q: 'How is this different from a YouTube playlist or a Udemy course?',
    a: "AdaptOS notices when you're struggling and responds. YouTube and Udemy explain. They don't change the angle when you miss something. They don't give you targeted practice on exactly what tripped you up. They don't confirm you've repaired a misunderstanding before letting you advance. AdaptOS does all three — every concept, every lesson.",
  },
  {
    q: "What if I don't pass my exam?",
    a: "AdaptOS uses mastery gates — meaning you don't advance through lessons until the system confirms you actually understand the concept. That's not an exam guarantee, but it's the closest thing to one in adaptive learning. Completing the full track puts you in a dramatically stronger position than self-study.",
  },
  {
    q: 'Can I switch certification tracks?',
    a: 'Yes. Your subscription works across all available tracks. Switch any time without losing progress on the original track.',
  },
  {
    q: 'How much does it cost?',
    a: 'Try your first 3 lessons at no cost — no card required. After that, it\'s a flat $21.99/month for full access to every track. Cancel any time, no fee.',
  },
  {
    q: 'Do I need to be technical to start?',
    a: 'No. CompTIA A+ is the entry-level IT certification — built for people coming from non-technical backgrounds. AdaptOS adapts to where you are: starting from zero, the system teaches at that level. Already know things? It moves faster.',
  },
  {
    q: 'How long does it take to get certified?',
    a: 'Most learners finish A+ in 8–12 weeks at a comfortable pace (3–5 hours per week). Faster if you push, slower if life happens. AdaptOS adapts to your rhythm — there’s no fixed schedule to fall behind on.',
  },
  {
    q: "Some features aren't working — could my browser account be the problem?",
    a: "Possibly. If your browser is signed in with a work or school (Google Workspace) account, your organization's admin policies can block parts of AdaptOS — things like video playback, embedded content, or signing in. If something isn't loading, try a personal Google account, a separate browser profile, or an incognito/private window. That clears it up almost every time.",
  },
];

export default function FaqAndFinalCtaSection() {
  const [open, setOpen] = useState(null);

  return (
    <section className="fq-sec">
      <div className="fq-inner">
        <h3 className="fq-h3">Questions.</h3>

        <div className="fq-list">
          {FAQS.map((item, i) => {
            const isOpen = open === i;
            return (
              <div className={`fq-item${isOpen ? ' open' : ''}`} key={item.q}>
                <button
                  type="button"
                  className="fq-q"
                  aria-expanded={isOpen}
                  onClick={() => setOpen(isOpen ? null : i)}
                >
                  <span>{item.q}</span>
                  <span className="fq-icon" aria-hidden="true">{isOpen ? '−' : '+'}</span>
                </button>
                <div className="fq-ans-wrap">
                  <div className="fq-ans"><p>{item.a}</p></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Final CTA — hero-style closing moment */}
      <div className="fq-cta-wrap">
        <div className="fq-cta-inner">
          <h2 className="fq-cta-h2">Start learning today.</h2>
          <p className="fq-cta-sub">
            Try 3 lessons at no cost. No card required.<br />
            Then a flat $21.99/month for full access. Cancel any time.
          </p>
          <div className="fq-cta-actions">
            <a href="/registration" className="fq-cta-btn">Start Free Lessons</a>
          </div>
          <p className="fq-cta-note">Cancel any time — no fee.</p>
        </div>
      </div>

      <style>{`
        .fq-sec { position: relative; z-index: 1; padding: clamp(48px, 8vh, 96px) clamp(16px, 4vw, 40px) clamp(40px, 6vh, 72px); }
        .fq-inner { max-width: 760px; margin: 0 auto; }
        .fq-h3 {
          font-family: 'Fraunces', serif; font-weight: 500;
          font-size: clamp(24px, 3.4vw, 36px); line-height: 1.1;
          letter-spacing: -0.02em; margin: 0 0 clamp(24px, 4vw, 36px); text-align: center;
        }
        .fq-list { display: flex; flex-direction: column; gap: 12px; }
        .fq-item {
          background: #ffffff; box-shadow: 0 4px 20px rgba(0,0,0,0.05);
          border: 1px solid rgba(0,0,0,0.08); border-radius: 14px;
          overflow: hidden; transition: border-color .2s ease;
        }
        .fq-item.open { border-color: rgba(217,119,6,0.30); }
        .fq-q {
          width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 16px;
          background: none; border: none; cursor: pointer; text-align: left;
          padding: 18px 20px; color: #111110; font-family: inherit;
          font-size: clamp(15px, 1.8vw, 17px); font-weight: 600; line-height: 1.4;
        }
        .fq-icon { flex-shrink: 0; color: #d97706; font-size: 22px; font-weight: 400; line-height: 1; }
        .fq-ans-wrap { display: grid; grid-template-rows: 0fr; transition: grid-template-rows .3s ease; }
        .fq-item.open .fq-ans-wrap { grid-template-rows: 1fr; }
        .fq-ans { overflow: hidden; }
        .fq-ans p {
          margin: 0; padding: 0 20px 20px; font-size: 15px; line-height: 1.6;
          color: rgba(17,17,16,0.68); font-weight: 300;
        }
        .fq-cta-wrap {
          position: relative; z-index: 1; margin-top: clamp(40px, 6vw, 64px);
          padding: clamp(56px, 9vh, 110px) clamp(16px, 4vw, 40px);
          background:
            radial-gradient(120% 120% at 50% 0%, rgba(217,119,6,0.12) 0%, transparent 60%),
            rgba(0,0,0,0.02);
          border-top: 1px solid rgba(0,0,0,0.06);
        }
        .fq-cta-inner { max-width: 640px; margin: 0 auto; text-align: center; }
        .fq-cta-h2 {
          font-family: 'Fraunces', serif; font-weight: 500;
          font-size: clamp(30px, 5vw, 52px); line-height: 1.06;
          letter-spacing: -0.02em; margin: 0;
        }
        .fq-cta-sub {
          font-weight: 300; color: rgba(17,17,16,0.66);
          font-size: clamp(17px, 2.2vw, 22px); margin: 18px auto 0; max-width: 40ch;
        }
        .fq-cta-actions { display: flex; flex-wrap: wrap; gap: 14px; justify-content: center; margin-top: 32px; }
        .fq-cta-btn {
          display: inline-flex; align-items: center; justify-content: center;
          background: linear-gradient(135deg, #f59e0b, #d97706); color: #ffffff;
          font-size: 17px; font-weight: 700; padding: 17px 38px; border-radius: 12px;
          text-decoration: none; box-shadow: 0 8px 26px rgba(217,119,6,0.34);
          transition: transform .18s ease, box-shadow .18s ease;
        }
        .fq-cta-btn:hover { transform: translateY(-2px); box-shadow: 0 12px 34px rgba(217,119,6,0.46); }
        .fq-cta-btn2 {
          display: inline-flex; align-items: center; justify-content: center;
          background: transparent; color: #111110; border: 1.5px solid rgba(0,0,0,0.22);
          font-size: 17px; font-weight: 600; padding: 17px 38px; border-radius: 12px; text-decoration: none;
          transition: border-color .18s ease, color .18s ease, background .18s ease;
        }
        .fq-cta-btn2:hover { border-color: #d97706; color: #d97706; background: rgba(217,119,6,0.06); }
        .fq-cta-note { margin: 18px 0 0; font-size: 14px; color: rgba(17,17,16,0.5); }
        @media (max-width: 560px) {
          .fq-cta-actions { flex-direction: column; }
          .fq-cta-btn, .fq-cta-btn2 { width: 100%; }
        }
      `}</style>
    </section>
  );
}
